import { HttpErrorResponse } from '@angular/common/http';
import { EntityState } from '@ngrx/entity';

export const FEATURE_KEY = 'securityManagementFeature';

export interface ISecurityManagementState {
  demographicInfo: {
    results: IChildUserRoleDetails | IUserPreferencesResponse;
    loading: boolean;
    error: any;
  };
  securitySets: ISecuritySetState;
  pendingUsers: IPendingUserState;
  isAddNewUserBtnActive: boolean;
  addUserSearchCriteria: IAddUserSearchCriteria;
  addUserSearchResults: IAddUserSearchResult;
  editUserSearchCriteria: IExistingUsersSearchCriteria;
  editUserSearchResults: IEditUserSearchResultRes;
  addUserSearchState: IAddUserSearchState;
  existingUserInfo: {
    demographicInfo: IUserPreferencesResponse;
    error: HttpErrorResponse;
    isLoading: boolean;
    // other sub-props of existingUser info:
    childUserInfo: IEditUserSearchResult;
  };
  editUserRemoveAction: IEditUserRemoveActionState;
  editUserActiveSet: IEditUserActiveSecuritySet;
  assignUserRole: IAssignUserRolePage;
  landingPageRoleData: IassignRoleLandingPage[];
  currentScreen: TAddEditUserScreenType;
  vCodeData: IVCodeGeneralLevelRes;
  eachVcodeData: IGetEachVcodeDetailRes[];
  pagePosition: { position: number };
  submissionDetails: any;
  submissionState: ISubmissionState;
  listenAssignRolesApi: string[];
  groupRolesApiCallsdDone: TGroupRolesApisDone;
}

export interface ISecuritySetState {
  results: ISecurityGroupSet[];
  currentSecuritySet: ISecurityGroupSet;
  loading: boolean;
  error: HttpErrorResponse;
}

export interface ISecurityGroupSet {
  id: string;
  name: string;
  oaaCount?: number;
  childCount?: number;
  maxOaaCount?: number;
  dateAdded?: string;
  isChecked?: boolean;
  taxIds?: string[];
}

export interface ISubmissionState {
  submitting: boolean;
  submissionSuccess: boolean;
  submissionError: boolean;
}

export interface ISelectedRolesSummary {
  selectedProviders: EntityState<IVcodeIndividualData>;
  selectedOffices: EntityState<IVcodeIndividualData>;
  selectedClaimsub: EntityState<IVcodeIndividualData>;
  selectedClaimshcfa: EntityState<IVcodeIndividualData>;
  selectedFinancialIdentifiers: EntityState<IVcodeIndividualData>;
  selectedChiropracticIdentifiers: EntityState<IVcodeIndividualData>;
  selectedHeliosIdentifiers: EntityState<IVcodeIndividualData>;
}

export interface IAddUserSearchCriteria {
  firstName?: string;
  lastName?: string;
  email?: string;
  userId?: string;
  added?: string;
  securityGroupId?: string;
}

export interface IAddUserSearchState {
  addUserSearchCriteria?: IAddUserSearchCriteria;
  addUserSearchResults?: IAddUserSearchResult;
  currentPage?: number;
  sortDir?: number;
  sortCol?: string;
  addUserPagePosition?: number;
  pageSize?: number;
}

export interface IAddUserSearchResult {
  result: IOAAUser[];
  isLoading: boolean;
  error: HttpErrorResponse;
}

export interface IOAAUser {
  userId: string;
  lastName: string;
  firstName: string;
  email: string;
  addedforTable: string;
  added: string;
}

export interface IExistingUsersSearchCriteria {
  userId?: string;
  lastName?: string;
  firstName?: string;
  email?: string;
  userNameSearchParam?: string;
  added?: any[];
}

export interface IEditUserSearchResultRes {
  result: IEditUserSearchResult[];
  currentUsers: IEditUserSearchResult[];
  isLoading: boolean;
  error: any;
}

export interface IEditUserSearchResult {
  userId?: string;
  lastName?: string;
  firstName?: string;
  added?: ISecurityGroupSet[];
  email?: string;
  addedforTable?: string;
}

export interface IEditUserRemoveRequestData {
  preferredUserName: string;
  securityGroupIds: string[];
}

// remove action
export interface IEditUserRemoveActionState {
  isSuccess: boolean;
  isLoading: boolean;
  actionType: IEditUserAction;
  error: any;
}

export interface IEditUserActiveSecuritySet {
  results: IEditUserActiveResponse[];
  isLoading: boolean;
  error: any;
}

export interface IEditUserActiveResponse {
  securityGroupIds: IEditUserSecurityGroupIds[];
}

export interface IEditUserSecurityGroupIds {
  securityGroupId: string;
  isOaa: boolean;
}
export interface IEditUserResponseType<ReponseType> {
  results: ReponseType;
}

export type IEditUserAction = null | 'add' | 'save' | 'remove';

export interface IChildUserRoleDetails {
  childUser: IChildUserResponse;
  securityGroup: {
    id: string;
    oaaRequestId: string;
    name: string;
    status: string;
    partitionKey: string;
    providerType: string;
    tpaClient: string;
    tpaId: string;
    tpaDiv: string;
    created: string;
    taxIds: {
      taxId: string;
      vcodes: {
        name: string;
        number: string;
        providerCount: 0;
        siteCount: 0;
        isPar: true;
      }[];
    }[];
    configuredPermissions: string[];
    oaas: {
      preferredUserName: string;
    }[];
    childUsers: [
      {
        preferredUserName: string;
      }
    ];
    isPar: true;
  };
  navinetIds: [];
  invitationId?: string,
  userPreferences: IUserPreferencesResponse;
  roleAssignmentConfig: IRoleAssignmentConfigResponse[];
  sensitiveInformation?: {
    canViewVip: boolean;
    isInternalUser: boolean;
    sensitiveInformation?: string[];
  };
  hasSelectedClaims?: boolean;
  hasSelectedProviders?: boolean;
  hasSelectedOffices?: boolean;
  hasSelectedFinancials?: boolean;
}

export interface ISecurityGroupResponse {
  id: string;
  oaaRequestId: string;
  name: string;
  status: string;
  partitionKey: string;
  providerType: string;
  tpaClient: string;
  tpaId: string;
  tpaDiv: string;
  created: string;
  isPar: boolean;
  taxIds: {
    taxId: string;
    vcodes: {
      name: string;
      number: string;
      providerCount: number;
      siteCount: number;
      isPar: boolean;
    }[];
  }[];
  configuredPermissions: string[];
  oaas: {
    preferredUserName: string;
  }[];
}

export interface IChildUserResponse {
  dateAdded: string;
  documentType: string;
  id: string;
  isActiveInTheGroup: boolean;
  navinetUserIds: string[];
  partitionKey: string;
  preferredUserName: string;
  invitationId?: string;
}

export interface IUserPreferencesResponse {
  address1: string;
  address2: string;
  city: string;
  email: string;
  firstName: string;
  id: string; //
  lastName: string;
  middleInitial: string;
  phoneNumber: string;
  phoneNumberExtension: string;
  workPhoneExtension?: string;
  preferredUserName: string; //
  invitationId?: string;
  state: string;
  zip: string;
  isPhysician?: boolean;
  npiNumber?: string;
  canViewVip?: boolean; //
  isInternalUser?: boolean; //
  securityGroupId?: string;
}

export interface IAddEditUserForm {
  firstName: string;
  lastName: string;
  middleInitial?: string;
  isPhysician: boolean;
  npiNumber?: string;
  email: string;
  workPhone?: string;
  phoneNumber?: string;
  workPhoneExtension?: string;
  phoneNumberExtension?: string; // two vars for ext, hope can use one
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  userId?: string;
  securityGroupId?: string;
  userName?: string; // editUserForm
}

export interface ISecuritySetModifier {
  sensitiveDX: string[]; // not sure now
  vipAccess: boolean | string;
  navinetID: string[]; // not sure now
  isLoading: boolean;
}

export interface IRoleFeatureDetails {
  [key: string]: {
    hasFeature: boolean;
    featureDetail?: string;
    defaultFeature?: string;
  };
}

export interface IRoleFeatures {
  hasRole: boolean;
  features: IRoleFeatureDetails;
}

export interface IRoleAndFeatures {
  [key: string]: IRoleFeatures;
}

export interface IAssignUserRolePage {
  isPar: boolean;
  // userDemographic: IUserPreferencesResponse;
  securityModifier: ISecuritySetModifier;
  roleAndFeatures: IRoleAndFeatures;
} // todo: this interface is temporary one, accurate one is based on submit endpoint

export interface IRoleAssignmentConfigResponse {
  display: TUserRole;
  isReadOnly: boolean;
  isCheckedByDefault: boolean;
  roleFeatures: IRoleAssignConfigRoleFeatures[];
  sortOrder: number;
  uiType: TUserRoleUIType;
  userAssignments: {
    hasAccess: boolean;
    permissionName: string;
  }[];
  tooltip: string;
}

export interface IRoleAssignConfigRoleFeatures {
  display: string;
  groupingId: string;
  isReadOnly: boolean;
  sortOrder: number;
  subFeatures: {
    display: string;
    isReadOnly: boolean;
    sortOrder: number;
    uiType: string;
    userAssignments: {
      hasAccess: boolean;
      permissionName: string;
    }[];
  }[];
  uiType: 'text';
  userAssignments: {
    hasAccess: boolean;
    permissionName: string;
  }[];
}

// export interface IassignRoleInFormGroup {
//   Key: {hasFeature: boolean; key: {} }
// }
export interface IassignRoleLandingPage {
  name: string;
  isAssignedRole: any;
  hasRole: any;
  children: {
    name: string;
    hasRole: any;
    roleDetails: any[];
  }[];
}

export interface IVCodeGeneralLevelRes {
  generalResult: IVCodeGeneralLevel;
  loading: boolean;
  error: HttpErrorResponse;
}

export interface IVCodeGeneralLevel {
  results?: IVcodeIndividualData[];
  financials?: IVcodeIndividualData[];
  chiropractic?: IVcodeIndividualData[];
  ub?: IVcodeIndividualData[] | any[];
  hcfa?: IVcodeIndividualData[] | any[];
  unknown?: IVcodeIndividualData[] | any[];
  officeResults?: IVcodeIndividualData[];
  providers?: IVcodeIndividualData[];
  offices?: IVcodeIndividualData[];
  helios?: IVcodeIndividualData[];
}

export interface IVcodeIndividualData {
  isChecked?: boolean;
  isExpanded?: boolean;
  name: string;
  offices?: IDetailsOfEachOfficeVcode[];
  taxId: string;
  totalProviderCount?: number;
  userAssignment: {
    permissionName: string;
    hasAccess: boolean;
  };
  usersProviderCount: number;
  vcode: string;
  selectedProviderCount?: number;
  providerIDcardList?: IDetailsOfEachProviderVcode[];
  lastNameChars?: string[];
  totalOfficeCount?: number;
  usersOfficeCount?: number;
  selectedOfficeCount?: number;
  showExceedWarning?: boolean;
  showAlphaList?: boolean;
  selectedAlpha?: string | null;
  providers?: IDetailsOfEachProviderVcode[];
  specialties?: {
    specialtyName?: string;
    isSelected?: boolean;
  }[];
}

export interface IDetailsOfEachProviderVcode {
  npi: string;
  name: string;
  providerId: string;
  isChecked?: boolean;
  id?: string;
  userAssignment?: { hasAccess: boolean };
}

export interface IGetEachVcodeDetailRes {
  vCode: string;
  results: IDetailsOfEachProviderVcode[];
  lastNameChars: string[];
  showAlphaList: boolean;
  loading: boolean;
  error: HttpErrorResponse;
}

export interface IDetailsOfEachOfficeVcode {
  isChecked?: boolean;
  address1: string;
  city: string;
  officeId: string;
  officeName: string;
  state: string;
  userAssignment: { hasAccess: boolean };
  zip: string;
}

export interface ISelectedRolesSummaryForSubmission {
  selectedProviders: ISubmissionVcodeDetailsOfEachRole[];
  selectedOffices: ISubmissionVcodeDetailsOfEachRole[];
  selectedClaimsub: ISubmissionVcodeDetailsOfEachRole[];
  selectedClaimshcfa: ISubmissionVcodeDetailsOfEachRole[];
  selectedFinancialIdentifiers: ISubmissionVcodeDetailsOfEachRole[];
  selectedChiropracticIdentifiers: ISubmissionVcodeDetailsOfEachRole[];
  selectedHeliosIdentifiers: ISubmissionVcodeDetailsOfHeliosRole;
}

export interface ISubmissionBody {
  securityGroupId: string;
  childUserName?: string;
  invitationId?: string;
  navinetIds?: string[];
  vipAccess?: boolean;
  sensitiveInformation?: string[];
  selectedRoles?: string[];
  selectedProviders?: ISubmissionVcodeDetailsOfEachRole[];
  selectedOffices?: ISubmissionVcodeDetailsOfEachRole[];
  selectedClaims?: {
    ub?: ISubmissionVcodeDetailsOfEachRole[];
    hcfa?: ISubmissionVcodeDetailsOfEachRole[];
  };
  selectedFinanceIdentifiers?: ISubmissionVcodeDetailsOfEachRole[];
  selectedChiropracticIdentifiers?: ISubmissionVcodeDetailsOfEachRole[];
  selectedHeliosIdentifiers?: ISubmissionVcodeDetailsOfHeliosRole;
}

export interface ISubmissionVcodeDetailsOfEachRole {
  taxId: string;
  vcode?: string;
  npis?: string[];
  officeIds?: string[];
}

export interface ISubmissionVcodeDetailsOfHeliosRole {
  specialty: string;
  taxIds: string[];
}

export type TUserRoleUIType = 'text' | 'checkbox' | 'radio';

export type TUserRole = 'Basic' | 'Universal' | 'Claims' | 'Financial' | 'Clinical' | 'Marketing' | 'Reporting';

export type TAddEditUserScreenType = 'multi-sets' | 'assign-roles' | 'role-landing' | 'data-view' | 'summary';

export type TGroupRolesApisDone = 'pass' | 'loading' | 'ready';

export interface ISecurityGroupByOaaResponse {
  securityGroupsByOaa: ISecurityGroupSet[];
}

export interface IPendingUserState {
  results: IPendingUser[];
  loading: boolean;
  error: HttpErrorResponse;
}

export interface IPendingUserResponse {
  results: IPendingUser[];
}

export interface IPendingUser {
  invitationId: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  name: string;
  expiresAt: string;
  phoneNumber: string;
  created: string;
  providerType: string;
  systemName: string;
}

export type SaveContactPayload = {
  contactTypeId: number,
  firstName: string,
  lastName: string,
  email: string,
  taxId: string,
  securityGroupId: string,
  userName: string
};
